import React from 'react';

import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { CopyButton, Tooltip } from '@vlabs/uikit';
import type { WithClassNameProp } from '@vlabs/uikit/types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './LargeFaceCard.module.sass';
import { useGetFace } from '@vlabs/api/luna-api/client';
import type { OutputFace } from '@vlabs/api/luna-api/model';
import { buildTestid } from '../../../lib/utils';
import type { BaseFaceCardProps } from '../base-face-card/BaseFaceCard';
import { BaseFaceCard } from '../base-face-card/BaseFaceCard';

export interface LargeFaceCardProps extends
  Omit<BaseFaceCardProps, 'src' | 'size' | 'can'>,
  WithClassNameProp {
  actions?: React.JSX.Element | ((face: OutputFace) => JSX.Element);
}

const displayName = 'LargeFaceCard';
const testid = buildTestid(displayName);

export function LargeFaceCard({
  faceId,
  actions = undefined,
  'data-testid': dataTestId = undefined,
  className = undefined,
  ...props
}: LargeFaceCardProps): React.JSX.Element {
  const { t } = useTranslation();
  // @ts-expect-error проверка FaceId дальше
  const getFace = useGetFace(faceId, { targets: ['user_data', 'face_id', 'create_time', 'avatar'] }, {
    query: {
      enabled: !!faceId,
    },
  });
  const face = getFace.data;
  const userData = (face?.user_data?.length ?? 0) > 0 ? face.user_data : '-';

  return (
    <div
      className={classNames(st.Root, className)}
      {...testid(faceId, dataTestId)}
    >
      <BaseFaceCard
        faceId={face?.face_id}
        size="large"
        src={face?.avatar ? `/api/lp5${face?.avatar}` : undefined}
        {...props}
      />
      <div
        className={st.FaceCardData}
        {...testid(faceId, 'Data', dataTestId)}
      >
        <div className={st.DataRow}>
          <span
            className={st.DataLabel}
            {...testid(faceId, 'Data', 'CreateTime', 'Label', dataTestId)}
          >
            {t('monokit:face card.face.created')}
          </span>
          <span
            className={st.DataValue}
            {...testid(faceId, 'Data', 'CreateTime', 'Value', dataTestId)}
          >
            {parseISOToLocale(face?.create_time)}
          </span>
        </div>
        <div className={st.DataRow}>
          <span
            className={st.DataLabel}
            {...testid(faceId, 'Data', 'FaceId', 'Label', dataTestId)}
          >
            {t('monokit:face card.face.face id')}
          </span>
          <span
            className={classNames(st.DataValue, st.CopyFaceId)}
            {...testid(faceId, 'Data', 'FaceId', 'Value', dataTestId)}
          >
            <TruncatedText
              hasTruncation
              tooltipContent={face?.face_id}
            >
              {face?.face_id}
            </TruncatedText>
            <Tooltip
              position="bottom"
              tooltipContent={t('copy_label')}
            >
              <CopyButton
                className={st.CopyButton}
                round
                value={face?.face_id}
              />
            </Tooltip>
          </span>
        </div>
        <div className={st.DataRow}>
          <span
            className={st.DataLabel}
            {...testid(faceId, 'Data', 'UserData', 'Label', dataTestId)}
          >
            {t('monokit:face card.face.information')}
          </span>
          <span
            className={st.DataValue}
            {...testid(faceId, 'Data', 'UserData', 'Value', dataTestId)}
          >
            <TruncatedText
              hasTruncation
              tooltipContent={userData}
            >
              {userData}
            </TruncatedText>
          </span>
        </div>
      </div>
      {typeof actions === 'function' ? actions(face) : actions}
    </div>
  );
}
